<template>
	<div class="video-list">
		<VideoCenterHeader
			:title="title"
			:filters="Object.entries(filters).map(entry => entry[1]).filter(filter => filter !== null)"
			:hide-filters="hideFilters"
			@changeSport="handleSportChange"
			@changeDate="handleDateChange"
			@changeTeamCategory="handleTeamCategoryChange"
			@changeTeam="handleTeamChange"
			@changeSearch="handleSearch"
			@changeEventState="handleEventStateChange"
		/>
		<EventsSection
			:pagination="videos.pagination"
			:handle-pagination-change="handlePaginationChange"
			:events="videos.items"
			:is-loading="videos.isLoading"
			:show-results-counter="filters.search.value !== '' && typeof videos.pagination.total === 'number'"
		>
			<div
				v-if="showResultsCount"
				class="video-list__results-count"
			>
				{{ videos.pagination.total }} {{ $I18n.trans_choice('commons.video', videos.pagination.total) }}
			</div>
			<Select
				v-if="enableSortOrder"
				class="video-list__select-order"
				:options="sortOrder.options"
				:value="sortOrder.selected"
				:searchable="false"
				:clearable="false"
				:dark-theme="!$arenaConfig.isLightTheme"
				:rounded="true"
				@change="handleSortOrderChange"
			/>
		</EventsSection>
	</div>
</template>

<script>
import EventsSection from '@js/components/EventListing/EventsSection';
import VideoCenterHeader from '@js/components/VideoCenter/Header';
import Select from '@shared/components/Select';
import { EventFilter } from '@shared/enums/EventFilter';
import { EventOrder } from '@shared/enums/EventOrder';
import { debounce } from '@shared/utils/debounce';
import { EventType } from '@shared/enums/EventType';
import { httpClient } from '@shared/js/api/httpClient';

export default {
	name:       'VideoList',
	components: { EventsSection, Select, VideoCenterHeader },
	props:      {
		title: {
			type:    String,
			default: null,
		},
		enableSearch: {
			type:    Boolean,
			default: true,
		},
		enableFilterDate: {
			type:    Boolean,
			default: true,
		},
		enableFilterSport: {
			type:    Boolean,
			default: true,
		},
		enableFilterTeamCategory: {
			type:    Boolean,
			default: false,
		},
		enableFilterTeam: {
			type:    Boolean,
			default: false,
		},
		enableSortOrder: {
			type:    Boolean,
			default: true,
		},
		enableEventStateToggle: {
			type:    Boolean,
			default: true,
		},
		contextFilter: {
			type:     Object,
			required: true,
		},
		eventFilters: {
			type:    Object,
			default: () => ({}),
		},
		externalSortOrder: {
			type:    Object,
			default: () => ({}),
		},
		showResultsCount: {
			type:    Boolean,
			default: false,
		},
		sports: {
			type:    Array,
			default: () => [],
		},
		contentPeriod: {
			type:    Array,
			default: () => [],
		},
		teamCategories: {
			type:    Array,
			default: () => [],
		},
		teams: {
			type:    Array,
			default: () => [],
		},
		hideFilters: {
			type:    Boolean,
			default: false,
		},
		useDivisionAsTeamCategoryLabel: {
			type:    Boolean,
			default: false,
		},
	},
	data() {
		return {
			filters: {
				sport: this.enableFilterSport
					? {
						type:         'select',
						id:           'sport',
						label:        this.$I18n.trans_choice('commons.sport', 1),
						defaultValue: 'all',
						emitEvent:    'changeSport',
						value:        'all',
						options:      [
							{
								label: this.$I18n.trans('commons.all_types', { type: this.$I18n.trans_choice('commons.sport', 2) }),
								value: 'all',
							},
							...this.sports,
						],
					}
					: null,
				date: this.enableFilterDate
					? {
						type:         'select',
						id:           'date',
						label:        this.$I18n.trans('commons.date'),
						defaultValue: 'all',
						emitEvent:    'changeDate',
						value:        'all',
						options:      [
							{
								label: this.$I18n.trans('commons.all_types', { type: this.$I18n.trans_choice('commons.year', 2) }),
								value: 'all',
							},
							...this.contentPeriod.map(year => ({
								label: year.toString(),
								value: year.toString(),
							})).reverse(),
						],
					}
					: null,
				teamCategory: this.enableFilterTeamCategory
					? {
						type:         'select',
						id:           'teamCategory',
						label:        this.useDivisionAsTeamCategoryLabel ? this.$I18n.trans_choice('commons.division', 1) : this.$I18n.trans('commons.category'),
						defaultValue: 'all',
						emitEvent:    'changeTeamCategory',
						value:        'all',
						options:      [
							{
								label: this.$I18n.trans('commons.all_types', { type: this.useDivisionAsTeamCategoryLabel ? this.$I18n.trans_choice('commons.division', 2) : this.$I18n.trans('commons.category') }),
								value: 'all',
							},
							...this.teamCategories.map(category => ({
								label: this.$I18n.trans(`team.category.${category}`),
								value: category,
							})).sort((a, b) => a.label < b.label ? -1 : 1),
						],
					}
					: null,
				team: this.enableFilterTeam
					? {
						type:         'select',
						id:           'team',
						label:        this.$I18n.trans_choice('commons.team', 1),
						defaultValue: 'all',
						emitEvent:    'changeTeam',
						value:        'all',
						options:      [
							{
								label: this.$I18n.trans('commons.all_types', { type: this.$I18n.trans_choice('commons.team', 2) }),
								value: 'all',
							},
							...this.teams.map(team => ({ label: team.name, value: team.id })).sort((a, b) => a.label < b.label ? -1 : 1),
						],
					}
					: null,
				search: this.enableSearch
					? {
						type:         'search',
						id:           'search',
						defaultValue: '',
						emitEvent:    'changeSearch',
						value:        '',
					}
					: null,
				eventState: this.enableEventStateToggle
					? {
						type:    'toggle',
						id:      'eventType',
						options: [
							{
								name: this.$I18n.trans('commons.all'),
								id:   'all',
							},
							{
								name: this.$I18n.trans('commons.live_upcoming_short'),
								id:   'not_ended',
							},
							{
								name: this.$I18n.trans_choice('commons.replay', 2),
								id:   'ended',
							},
						],
						value:        'all',
						defaultValue: 'all',
						emitEvent:    'changeEventState',
					}
					: {
						value: 'all',
					},
			},
			sortOrder: {
				options: [
					{
						label: this.$I18n.trans('commons.date'),
						value: 'date',
					},
					{
						label: this.$I18n.trans('commons.views'),
						value: 'views',
					},
				],
				selected:  'date',
				direction: 'desc',
			},
			videos: {
				isLoading:  false,
				pagination: this.getInitialPagination(24),
				items:      [],
			},
			debounceSearch: null,
		};
	},
	beforeMount() {
		this.applyURLParameters();
	},
	created() {
		this.debounceSearch = debounce(this.getVideos, 300, false);
	},
	mounted() {
		if (this.externalSortOrder && Object.keys(this.externalSortOrder).includes('start')) {
			this.sortOrder.selected = 'date';
			this.sortOrder.direction = this.externalSortOrder.start;
		}

		this.getVideos();
	},
	methods: {
		resetPaginationAndFetch(perPage = 24) {
			this.videos.pagination.currentPage = 1;
			this.videos.pagination.perPage = perPage;
			this.getVideos();
		},
		handlePaginationChange(page) {
			this.videos.pagination.currentPage = page;
			this.getVideos();
		},
		handleSortOrderChange(value) {
			this.sortOrder.selected = value;
			this.resetPaginationAndFetch();
		},
		handleSportChange(sportId) {
			this.filters.sport.value = sportId;
			this.resetPaginationAndFetch();
		},
		handleDateChange(date) {
			this.filters.date.value = date;
			this.resetPaginationAndFetch();
		},
		handleTeamCategoryChange(category) {
			this.filters.teamCategory.value = category;
			this.resetPaginationAndFetch();
		},
		handleTeamChange(teamId) {
			this.filters.team.value = teamId;
			this.resetPaginationAndFetch();
		},
		handleSearch(value) {
			this.filters.search.value = value;
			const perPage = value ? 12 : 24; // reset to 24 when search is empty
			this.videos.pagination = this.getInitialPagination(perPage);
			this.debounceSearch();
		},
		handleEventStateChange(value) {
			this.filters.eventState.value = value;
			this.resetPaginationAndFetch();
		},
		async fetchData(searchParams) {
			const response = await httpClient.getEventsCached(Object.fromEntries(new URLSearchParams(searchParams)));
			return response.data;
		},
		async getVideos() {
			try {
				this.videos.isLoading = true;

				const searchParams = this.getSearchParams();
				const data = await this.fetchData(searchParams);
				this.videos.items = data.data;
				this.videos.pagination = data.meta;
			} catch (error) {
				// shows empty list
			} finally {
				this.videos.isLoading = false;
			}
		},
		getSearchParams() {
			const searchParams = new URLSearchParams();

			// filter(s) parameter
			const filters = this.eventFilters || {};
			filters[EventFilter.ARENA_AVAILABLE] = null;

			// Apply event state filter
			switch (this.filters.eventState.value) {
			case 'all':
				filters[EventFilter.ENDED] = null;
				break;
			case 'not_ended':
				filters[EventFilter.EVENT] = null;
				filters[EventFilter.NOT_ENDED] = null;
				break;
			case 'ended':
				filters[EventFilter.CONTENT_TYPE] = [EventType.EVENT];
				filters[EventFilter.ENDED] = null;
				break;
			default:
				break;
			}

			// Apply context filter
			filters[this.contextFilter.key] = this.contextFilter.value;

			// Apply additional filters from parent (validated JSON object from query)
			if (this.additionalFilters && typeof this.additionalFilters === 'object' && !Array.isArray(this.additionalFilters)) {
				Object.entries(this.additionalFilters).forEach(([key, value]) => {
					filters[key] = value;
				});
			}

			// Date filter
			if (this.enableFilterDate && this.filters.date.value !== 'all') {
				const startOfYear = new Date(this.filters.date.value);
				startOfYear.setMonth(0);
				startOfYear.setDate(1);
				startOfYear.setHours(0, 0, 0, 0);
				const endOfYear = new Date(this.filters.date.value);
				endOfYear.setMonth(11);
				endOfYear.setDate(31);
				endOfYear.setHours(23, 59, 59, 59);
				const startFilterString = moment(new Date(startOfYear), 'DD.MM.YYYY').toISOString(true);
				const endFilterString = moment(new Date(endOfYear), 'DD.MM.YYYY').toISOString(true);

				filters[EventFilter.BETWEEN] = [startFilterString, endFilterString];
			}

			// Sport filter
			if (this.enableFilterSport && this.filters.sport.value !== 'all') {
				filters[EventFilter.SPORT] = this.filters.sport.value;
			}

			// Team category filter
			if (this.enableFilterTeamCategory && this.filters.teamCategory.value !== 'all') {
				filters[EventFilter.TEAM_CATEGORY] = this.filters.teamCategory.value;
			}

			// Team filter
			if (this.enableFilterTeam && this.filters.team.value !== 'all') {
				filters[EventFilter.TEAM] = this.filters.team.value;
			}

			// Search
			if (this.enableSearch && this.filters.search.value) {
				filters[EventFilter.SEARCH] = this.filters.search.value;
			}

			searchParams.append('filters', JSON.stringify(filters));

			if (this.enableSortOrder && this.sortOrder.selected === 'views') {
				searchParams.append('sortOrder', JSON.stringify({
					[EventOrder.VIEWS]: 'desc',
				}));
			} else if (this.filters.eventState.value === 'not_ended') {
				searchParams.append('sortOrder', JSON.stringify({
					[EventOrder.START]: 'asc',
				}));
			} else {
				searchParams.append('sortOrder', JSON.stringify({
					[EventOrder.START]: this.sortOrder.direction,
				}));
			}

			// pagination parameters
			searchParams.append('page', JSON.stringify(this.videos.pagination.currentPage));
			searchParams.append('perPage', JSON.stringify(this.videos.pagination.perPage));

			return searchParams;
		},
		getInitialPagination(perPage) {
			return {
				perPage:     perPage,
				currentPage: 1,
			};
		},
		applyURLParameters() {
			const urlParams = new URLSearchParams(window.location.search);
			const eventState = urlParams.get('eventState');
			if (eventState && this.filters.eventState.options.find(o => o.id === eventState)) {
				this.filters.eventState.value = eventState;
			}
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@sass/variables';

.video-list {
	margin-top: $sp6;
	margin-bottom: $sp7;

	@include size(md-up) {
		margin-top: $sp8;
		margin-bottom: $sp9;
	}
}

.video-list__results-count {
	@include font(primary, book, normal, fs-80);
	color: var(--color-grey-300);
	margin-bottom: 0;
}

.video-list__select-order {
	margin-bottom: $sp5;
	margin-left: auto;
	width: 200px;

	@include size(md-up) {
		width: 240px;
	}
}
</style>
